import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes, type ElementType, type ReactNode, type CSSProperties, type Ref } from 'react';
import { clsx } from 'clsx';
import { buttonFullWidth, buttonStartIcon, buttonEndIcon, buttonLabel } from './button.css';
import { wrapTextChild } from '../wrapTextChild';

interface ButtonBaseProps {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  style?: CSSProperties;
  as?: ElementType;
  // Link props (used when as='a' or href provided)
  href?: string;
  target?: string;
  download?: boolean | string;
}

export type ButtonProps = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps>;

/** Wrap string/number children; element children pass through. See wrapTextChild. */
function wrapLabel(children: React.ReactNode): React.ReactNode {
  return wrapTextChild(children, buttonLabel);
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button({
  as,
  href,
  target,
  download,
  startIcon,
  endIcon,
  fullWidth = false,
  className,
  children,
  style,
  ...rest
}, ref) {
  const Tag = (as ?? (href ? 'a' : 'button')) as ElementType;

  // Tabnabbing guard: default rel when opening in a new tab so callers can't forget.
  const { rel: explicitRel, type: explicitType, ...restWithoutRel } = rest as Record<string, unknown> & {
    rel?: string;
    type?: 'button' | 'submit' | 'reset';
  };
  const rel = explicitRel ?? (target === '_blank' ? 'noopener noreferrer' : undefined);

  // Default native buttons to type="button". A bare <button> defaults to
  // type="submit" (HTML spec), so a Button rendered inside a <form> with no
  // explicit type silently becomes a submit button (and an extra implicit
  // submitter). Callers that want submit behavior pass type="submit", which
  // overrides this default.
  const buttonType = Tag === 'button' ? (explicitType ?? 'button') : explicitType;

  return (
    <Tag
      ref={ref as Ref<HTMLElement>}
      type={buttonType}
      href={href}
      target={target}
      rel={rel}
      download={download}
      className={clsx(fullWidth && buttonFullWidth, className)}
      style={style}
      {...restWithoutRel}
    >
      {startIcon && <span className={buttonStartIcon}>{startIcon}</span>}
      {wrapLabel(children)}
      {endIcon && <span className={buttonEndIcon}>{endIcon}</span>}
    </Tag>
  );
});

Button.displayName = 'Button';
